// A new comment on the selected text (#13194, artboard 3): the caller's
// initials and name, the quote it will be anchored to, the field, Cancel and
// Comment. The field takes focus on open, except where a soft keyboard would
// rise over the document (rules/frontend.md, small screens). The document's
// creator also gets "Send to Finpilot" (PR 2): off on a new thread, checked
// by typing the agent's mention or by hand.
import React, { useState } from "react";
import { Tooltip } from "@mui/material";
import { ArrowUp } from "lucide-react";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { hasSoftKeyboard } from "@/utils/softKeyboard";
import { initialsOf } from "./DocumentCommentThread";
import { COMMENTS_UI_ATTR } from "../FpEditor/services/documentCommentsBridge";
import {
  AgentMentionChip,
  SendToAgentCheckbox,
  isSendKey,
  useAgentMention,
  useChipIndent,
} from "./sendToAgent";

const DocumentCommentComposer: React.FC<{
  quote: string;
  onCancel: () => void;
  /** Post the comment, sent to the agent or not; the composer closes when
   *  it lands. */
  onSubmit: (body: string, sendToAgent: boolean) => Promise<unknown>;
  /** The agent's name when the caller may send it the comment. */
  sendToAgentAs?: string | null;
  margin?: boolean;
}> = ({ quote, onCancel, onSubmit, sendToAgentAs = null, margin = false }) => {
  const { data: viewer } = useCurrentUser();
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);
  const [autoFocus] = useState(() => !hasSoftKeyboard());
  const agent = useAgentMention(sendToAgentAs, false);
  const chip = useChipIndent();
  const armed = body.trim().length > 0 && !sending;
  const name = viewer?.name ?? "";

  const submit = async () => {
    if (!armed) return;
    setSending(true);
    setFailed(false);
    try {
      await onSubmit(agent.compose(body), agent.send);
    } catch {
      setFailed(true);
    } finally {
      setSending(false);
    }
  };

  return (
    <form
      aria-label="New comment"
      {...{ [COMMENTS_UI_ATTR]: "" }}
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
      className={`flex flex-col gap-2.5 rounded-[10px] border border-[rgba(42,61,203,0.35)] bg-[var(--fp-surface)] px-3.5 py-3 [box-shadow:var(--fp-lift)] ${
        margin ? "w-[236px]" : "w-full"
      }`}
    >
      <div className="flex min-w-0 items-center gap-2">
        <span
          aria-hidden="true"
          className="inline-flex h-6 w-6 flex-none items-center justify-center rounded-full bg-[#d8d5cd] text-[10px] font-semibold tracking-[0.02em] text-[var(--fp-ink)]"
        >
          {initialsOf(name)}
        </span>
        <span className="min-w-0 truncate text-[13px] font-semibold text-[var(--fp-ink)]">
          {name}
        </span>
      </div>
      <div className="truncate border-l-2 border-[#E9C96A] pl-2 text-[12.5px] leading-[1.4] text-[#5a6068]">
        {quote}
      </div>
      {/* One box: the text on top, and inside the same border the row with
          "Send to <agent>" at the left and Cancel and the send arrow at the
          right. */}
      <div
        data-comment-field=""
        className="flex min-w-0 flex-col gap-1 rounded-[10px] border border-[var(--fp-border-strong)] px-2.5 pb-1.5 pt-2 focus-within:border-[var(--fp-ink)]"
      >
        <div className="relative min-w-0">
          {agent.mention && sendToAgentAs && (
            <span className="absolute left-0 top-0">
              <AgentMentionChip name={sendToAgentAs} chipRef={chip.chipRef} />
            </span>
          )}
          <textarea
            aria-label="Comment"
            value={body}
            rows={3}
            autoFocus={autoFocus}
            style={{ textIndent: agent.mention ? chip.indent : 0 }}
            onChange={(event) => setBody(agent.absorb(event.target.value))}
            onKeyDown={(event) => {
              if (
                event.key === "Backspace" &&
                agent.mention &&
                event.currentTarget.selectionStart === 0 &&
                event.currentTarget.selectionEnd === 0
              ) {
                event.preventDefault();
                agent.dropMention();
              } else if (isSendKey(event)) {
                event.preventDefault();
                void submit();
              } else if (event.key === "Escape") {
                event.preventDefault();
                // A post finishes with its composer on screen: it anchors when
                // it lands, or says it failed with the text kept.
                if (!sending) onCancel();
              }
            }}
            className="min-h-[60px] w-full min-w-0 flex-1 resize-none bg-transparent text-[13.5px] leading-[1.5] text-[var(--fp-ink)] outline-none"
          />
        </div>
        <div className="flex min-w-0 items-center gap-1">
          {sendToAgentAs && (
            <SendToAgentCheckbox
              name={sendToAgentAs}
              checked={agent.send}
              onToggle={agent.toggle}
              disabled={sending}
            />
          )}
          <span className="flex-1" />
          <button
            type="button"
            onClick={onCancel}
            disabled={sending}
            className="h-6 cursor-pointer rounded-[6px] px-1.5 text-[12.5px] font-medium text-[#5a6068] hover:bg-[var(--fp-bg-well)] disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent"
          >
            Cancel
          </button>
          <Tooltip title="Comment" arrow placement="top" describeChild>
            <span>
              <button
                type="submit"
                aria-label="Comment"
                disabled={!armed}
                className="inline-flex h-6 w-6 flex-none cursor-pointer items-center justify-center rounded-[6px] bg-gray-800 text-white disabled:cursor-default disabled:opacity-30"
              >
                <ArrowUp className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </span>
          </Tooltip>
        </div>
      </div>
      {failed && (
        <div role="alert" className="text-[12px] text-[var(--fp-danger)]">
          The comment was not saved.
        </div>
      )}
    </form>
  );
};

export default DocumentCommentComposer;
